import { Token, Trade } from '@monadex/sdk'
import React, { useCallback, useMemo } from 'react'
import {
  TransactionConfirmationModal,
  TransactionErrorContent,
  ConfirmationModalContent
} from '@/components'
import SwapModalHeader from './SwapModalHeader'
import { formatTokenAmount } from '@/utils'
import { useSwapState } from '@/state/swap/hooks'
import { Field } from '@/state/swap/actions'
import { calculateRemainingAmount, convertPercentageToAmount, tradeMeaningfullyDiffers } from '@/utils/calculateRaffleAmounts'

interface ConfirmSwapModalProps {
  isOpen: boolean
  trade?: Trade
  originalTrade?: Trade
  inputCurrency?: Token
  outputCurrency?: Token
  attemptingTxn: boolean
  txPending?: boolean
  txHash: string | undefined
  recipient: string | null
  allowedSlippage: number
  raffleFormattedAmount?: string
  raffleField?: Field
  onAcceptChanges: () => void
  onConfirm: () => void
  swapErrorMessage: string | undefined
  onDismiss: () => void
}

const ConfirmSwapModal: React.FC<ConfirmSwapModalProps> = ({
  trade,
  inputCurrency,
  outputCurrency,
  originalTrade,
  onAcceptChanges,
  allowedSlippage,
  onConfirm,
  onDismiss,
  swapErrorMessage,
  isOpen,
  attemptingTxn,
  txHash,
  txPending,
  raffleFormattedAmount,
  raffleField
}) => {
  const { fractionOfSwapAmount, enterRaffle } = useSwapState()
  const showAcceptChanges = useMemo(
    () =>
      Boolean(
        (trade != null) &&
        (originalTrade != null) &&
        tradeMeaningfullyDiffers(trade, originalTrade)
      ),
    [originalTrade, trade]
  )

  const amount1 = formatTokenAmount(trade?.inputAmount)
  const symbol1 = (trade != null)
    ? trade?.inputAmount?.currency?.symbol
    : inputCurrency?.symbol
  const amount2 = formatTokenAmount(trade?.outputAmount)
  const symbol2 = (trade != null)
    ? trade?.outputAmount?.currency?.symbol
    : outputCurrency?.symbol

  // Calculate the remaining amount that goes to wallet
  const remainngRaffleAmount = calculateRemainingAmount(fractionOfSwapAmount, raffleFormattedAmount)
  const fractionAmount = convertPercentageToAmount(fractionOfSwapAmount, raffleFormattedAmount)

  const amountInRaffle = raffleFormattedAmount !== undefined && raffleField && enterRaffle
    ? raffleField === Field.OUTPUT
      ? `${amount1} ${symbol1 ?? 'INVALID SYMBOL'}`
      : `${amount1} ${symbol1 ?? 'INVALID SYMBOL'} + ${fractionAmount ?? ''} ${symbol1 ?? 'INVALID SYMBOL'} (Raffles)`
    : `${amount1} ${symbol1 ?? 'INVALID SYMBOL'}`

  const amountOutRaffle = raffleFormattedAmount !== undefined && raffleField && enterRaffle
    ? raffleField === Field.OUTPUT
      ? `${remainngRaffleAmount ?? ''} ${symbol2 ?? 'INVALID SYMBOL'}`
      : `${amount2} ${symbol2 ?? 'INVALID SYMBOL'}`
    : `${amount2} ${symbol2 ?? 'INVALID SYMBOL'}`

  // text to show while loading
  const pendingText = `Swapping ${amountInRaffle} for ${amountOutRaffle}`

  const modalHeader = useCallback(() => {
    return (trade != null)
      ? (
        <SwapModalHeader
          trade={trade}
          inputCurrency={inputCurrency}
          outputCurrency={outputCurrency}
          allowedSlippage={allowedSlippage}
          onConfirm={onConfirm}
          showAcceptChanges={showAcceptChanges}
          onAcceptChanges={onAcceptChanges}
          amountInRaffle={amountInRaffle}
          amountOutRaffle={amountOutRaffle}
        />)
      : null
  }, [
    allowedSlippage,
    onAcceptChanges,
    showAcceptChanges,
    trade,
    onConfirm,
    inputCurrency,
    outputCurrency,
    amountInRaffle,
    amountOutRaffle
  ])

  const confirmationContent = useCallback(
    () =>
      swapErrorMessage !== undefined
        ? (
          <TransactionErrorContent
            onDismiss={onDismiss}
            message={swapErrorMessage}
          />)
        : (
          <ConfirmationModalContent
            id='confirm-swap-or-send'
            title='Confirm Transaction'
            onDismiss={onDismiss}
            content={modalHeader}
          />),
    [onDismiss, modalHeader, swapErrorMessage]
  )

  return (
    <TransactionConfirmationModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      txPending={txPending}
      content={confirmationContent}
      pendingText={pendingText}
      modalContent={txPending !== undefined ? 'Submitted transaction to swap your tokens' : 'Successfully swapped your tokens'}
    />
  )
}

export default ConfirmSwapModal
